import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../../store/root-reducer';
import { LeadRevealType } from '../../types';
import {
  openCreditNotAvailableModal,
  setSelectedLeads,
  setIsSelectedAllLeads,
  setSelectedCompanies,
  setIsSelectedAllCompanies,
  setShowRemoveFromListModal,
} from '../../v2-lead-slice';
import {
  leadFinderBulkOperationLimit,
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
} from '../../../../shared/utils/user-details';
import { AnalyticsEvents } from '../../../../shared/enums/analytics';

interface UseRowActionsProps {
  leadsData: { profiles: any[]; pagination: any };
  companiesData: { profiles: any[]; pagination: any };
  leadTags: any[];
  leadCredits: number;
  leadFinderBulkOperationLimitValue: number;
}

export const useRowActions = (props: UseRowActionsProps) => {
  const { leadsData, companiesData, leadTags, leadCredits, leadFinderBulkOperationLimitValue } = props;

  const dispatch = useDispatch();
  const { selectedLeads, isSelectedAllLeads } = useSelector(
    (state: RootState) => state.leadFinderV2.selection,
  );

  const [addToListOpenFromRow, setAddToListOpenFromRow] = useState(false);
  const [companyIdToAddFromRow, setCompanyIdToAddFromRow] = useState<number | null>(null);
  const [leadIdToAddFromRow, setLeadIdToAddFromRow] = useState<number | null>(null);
  const [triggerOpenAddTag, setTriggerOpenAddTag] = useState<number | null>(null);
  const [triggerOpenRemoveTag, setTriggerOpenRemoveTag] = useState<number | null>(null);

  const companyIdToAddFromRowRef = useRef<number | null>(null);
  const leadIdToAddFromRowRef = useRef<number | null>(null);

  const handleLeadRowRemoveFromList = useCallback((leadId: number) => {
    setLeadIdToAddFromRow(leadId);
    leadIdToAddFromRowRef.current = leadId;
    dispatch(setShowRemoveFromListModal(true));
  }, [dispatch]);

  const handleLeadRowAddTag = useCallback(
    (leadId: number, options?: { skipSelection?: boolean }) => {
      if (!options?.skipSelection) {
        dispatch(setSelectedLeads([leadId]));
        dispatch(setIsSelectedAllLeads(false));
      }
      setTriggerOpenAddTag(leadId);
    },
    [dispatch],
  );

  const handleLeadRowRemoveTag = useCallback(
    (leadId: number, options?: { skipSelection?: boolean }) => {
      if (!options?.skipSelection) {
        dispatch(setSelectedLeads([leadId]));
        dispatch(setIsSelectedAllLeads(false));
      }
      setTriggerOpenRemoveTag(leadId);
    },
    [dispatch],
  );

  const handleRowTagModalClosed = useCallback(() => {
    setTriggerOpenAddTag(null);
    setTriggerOpenRemoveTag(null);
  }, []);

  const handleAddToListOpenChange = useCallback(
    (open: boolean, options?: { preserveRowSelection?: boolean }) => {
      // Calculate count
      let count = 0;
      if (isSelectedAllLeads) {
        count =
          leadsData?.pagination?.totalRows > leadFinderBulkOperationLimitValue
            ? leadFinderBulkOperationLimitValue
            : leadsData?.pagination?.totalRows || 0;
      } else {
        // Count selected leads that are not revealed (or re-reveal for EMAIL_PHONE)
        const selectedLeadsData = leadsData?.profiles?.filter((lead: any) =>
          selectedLeads.includes(lead.id),
        );
        count =
          selectedLeadsData?.filter((l: any) => !l?.isRevealed)?.length || 0;
      }

      if (leadCredits < count) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.EMAIL));
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }

      setAddToListOpenFromRow(open);
      if (!open && !(options && options.preserveRowSelection)) {
        setCompanyIdToAddFromRow(null);
        setLeadIdToAddFromRow(null);
        companyIdToAddFromRowRef.current = null;
        leadIdToAddFromRowRef.current = null;
      }
    },
    [
      isSelectedAllLeads,
      leadsData?.pagination?.totalRows,
      leadsData?.profiles,
      selectedLeads,
      leadFinderBulkOperationLimitValue,
      leadCredits,
      dispatch,
    ],
  );

  const handleSingleLeadAddToList = useCallback(
    (leadId: number) => {
      if (leadCredits < 1) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.EMAIL));
        return;
      }
      setLeadIdToAddFromRow(leadId);
      leadIdToAddFromRowRef.current = leadId;
      setAddToListOpenFromRow(true);
    },
    [leadCredits, dispatch],
  );

  const overrideSelectedLeadObjects = useMemo(() => {
    if (
      (triggerOpenAddTag ?? triggerOpenRemoveTag) == null ||
      !leadsData?.profiles
    ) {
      return undefined;
    }
    const lid = triggerOpenAddTag ?? triggerOpenRemoveTag ?? 0;
    const lead = leadsData.profiles.find(
      (l: any) => Number(l?.id ?? l?.lead_id) === lid,
    );
    if (!lead) return undefined;
    const tagIds: number[] = [];
    if (lead?.tags && Array.isArray(lead.tags)) {
      lead.tags.forEach((t: any) => {
        const id = t?.id ?? t;
        if (id != null) tagIds.push(Number(id));
      });
    }
    if (tagIds.length === 0 && lead?.tagsIds && Array.isArray(lead.tagsIds)) {
      tagIds.push(...(lead.tagsIds as number[]));
    }
    const tags =
      tagIds.length > 0 && leadTags?.length
        ? tagIds
            .map((tagId) => leadTags.find((t: any) => t?.id === tagId))
            .filter(Boolean)
            .map((t: any) => ({ id: t.id, name: t.name }))
        : [];
    return [{ ...lead, id: lead.id, tags }];
  }, [triggerOpenAddTag, triggerOpenRemoveTag, leadsData?.profiles, leadTags]);

  const overrideSelectedCompanyObjects = useMemo(() => {
    if (
      (triggerOpenAddTag ?? triggerOpenRemoveTag) == null ||
      !companiesData?.profiles
    ) {
      return undefined;
    }
    const cid = triggerOpenAddTag ?? triggerOpenRemoveTag ?? 0;
    const company = companiesData.profiles.find(
      (c: any) => Number(c?.id ?? c?.company_id) === cid,
    );
    if (!company) return undefined;
    const companyId = company?.id ?? company?.company_id;
    const tagIds: number[] = [];
    if (company?.tags && Array.isArray(company.tags)) {
      company.tags.forEach((t: any) => {
        const id = t?.id ?? t;
        if (id != null) tagIds.push(Number(id));
      });
    }
    if (
      tagIds.length === 0 &&
      company?.tagsIds &&
      Array.isArray(company.tagsIds)
    ) {
      tagIds.push(...(company.tagsIds as number[]));
    }
    const tags =
      tagIds.length > 0 && leadTags?.length
        ? tagIds
            .map((tagId) => leadTags.find((t: any) => t?.id === tagId))
            .filter(Boolean)
            .map((t: any) => ({ id: t.id, name: t.name }))
        : [];
    return [{ ...company, id: companyId, tags }];
  }, [
    triggerOpenAddTag,
    triggerOpenRemoveTag,
    companiesData?.profiles,
    leadTags,
  ]);

  return {
    addToListOpenFromRow,
    setAddToListOpenFromRow,
    companyIdToAddFromRow,
    setCompanyIdToAddFromRow,
    leadIdToAddFromRow,
    setLeadIdToAddFromRow,
    triggerOpenAddTag,
    setTriggerOpenAddTag,
    triggerOpenRemoveTag,
    setTriggerOpenRemoveTag,
    companyIdToAddFromRowRef,
    leadIdToAddFromRowRef,
    handleLeadRowRemoveFromList,
    handleLeadRowAddTag,
    handleLeadRowRemoveTag,
    handleRowTagModalClosed,
    handleAddToListOpenChange,
    handleSingleLeadAddToList,
    overrideSelectedLeadObjects,
    overrideSelectedCompanyObjects,
  };
};
